import {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {useDispatch, useSelector} from 'react-redux';
import {setTimer} from "@arboxappv4/shared/src/redux/stepperSlice";
import {projectTypeMainSlice} from "@arboxappv4/shared/src/helpers/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_URGENT_SECONDS = 60;
const EXPIRED_REDIRECT_DELAY_MS = 5000;


const useCountdownTimer = ({onExpire}) => {
    const timer = useSelector(state => state.stepper.timer);
    const project_type = useSelector(state => state.project?.project_type);
    const selectedLocation = useSelector(state => state[projectTypeMainSlice[project_type]]?.selectedLocation);
    const [remainingMs, setRemainingMs] = useState(null);
    const tz = useMemo(() => selectedLocation?.timezone ?? null ,[selectedLocation])
    const dispatch = useDispatch()

    const effectiveEnd = useMemo(() => {
        if(!timer) return null
        const {endAt, leewayMinutes = 0} = timer
        if (!timer.endAt) return null;
        const base = tz ? dayjs.tz(endAt, tz) : dayjs(endAt);
        return base.add(leewayMinutes, 'minute');
    }, [timer]);

    const timeDisplay = useMemo(() => {
        if (remainingMs == null) return '--:--';
        const totalSec = Math.floor(remainingMs / 1000);
        const hh = Math.floor(totalSec / 3600);
        const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
        const ss = String(totalSec % 60).padStart(2, '0');
        return hh > 0 ? `${String(hh).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
    }, [remainingMs]);

    const urgentSeconds = timer?.urgentSeconds ?? DEFAULT_URGENT_SECONDS
    const isUrgent = useMemo(() => remainingMs != null && remainingMs <= urgentSeconds * 1000, [remainingMs, urgentSeconds]);
    const isExpired = useMemo(() => remainingMs === 0, [remainingMs]);

    useEffect(() => {
        if (!effectiveEnd || !effectiveEnd.isValid()) {
            setRemainingMs(null);
            return undefined;
        }
        const tick = () => setRemainingMs(Math.max(0, effectiveEnd.diff(dayjs())));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [effectiveEnd]);

    // spec: at 00:00 wait 5s, then show the expired-link page. Re-validating lets the
    // server return offerInvalidReason, which swaps this flow for WaitlistOfferExpired.
    useEffect(() => {
        if (!isExpired) return undefined;
        let id = null
        if(onExpire) {
            id = setTimeout(() => onExpire(), EXPIRED_REDIRECT_DELAY_MS);
        }
        return () => {
            if(id) clearTimeout(id)
            dispatch(setTimer(null))
        }
    }, [isExpired]);

    return {remainingMs, timeDisplay, isUrgent, isExpired, effectiveEnd};
};

export default useCountdownTimer;
